import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormField,
  Heading,
  InlineFeedback,
  Label,
  Modal,
  Paragraph,
  ShowHideWordCount,
  Textarea,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';
import { format } from 'date-fns';
import type React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useSatelliteWebLandlordConversationHandler_addMessage,
  useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata,
  useSatelliteWebLandlordConversationHandler_getLatestConversationsForContactsPage,
} from '@zg-rentals/ts-satellite-client-react-query';
import type {
  SatelliteWebLandlordGetLatestForContactsPageConversationDto,
  SatelliteWebLandlordGetLatestForContactsPageResponseDto,
} from '@zg-rentals/ts-satellite-client';
import StringConstants from '../../../constants/StringConstants';
import useModal from '../../../hooks/useModal';
import { AppContext } from '../../../contexts/AppContextProvider';
import { useContactsPageConversationsQueryParams } from '../../../hooks/useContactsPageConversationsQueryParams';
import formValidationUtils from '../../../utils/formValidationUtils';
import stringUtils from '../../../utils/stringUtils';
import { LOCAL_STORAGE_KEY_PREFIX } from '@zg-rentals/satellite-components';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import { BASE_PATH } from '../../../constants/sharedConfig';
import {
  localStorageSafeGetItem,
  localStorageSafeRemoveItem,
  localStorageSafeSetItem,
} from '@zg-rentals/satellite-components';
import { appMarkerLogger } from '../../../bootstrap';

const logger = appMarkerLogger.child({ name: 'SendMessageModal' });

type SendMessageModalProps = {
  conversationId: string;
  listingAlias: string;
  // Conversation data from LeadsTable
  conversation?: SatelliteWebLandlordGetLatestForContactsPageConversationDto;
  // Pagination info for targeted cache update
  currentRowsPerPage: number;
  currentStart: number;
};

const VISIBLE_WORDS_COUNT = 32;

export default function SendMessageModal({
  conversationId,
  listingAlias,
  conversation,
  currentRowsPerPage,
  currentStart,
}: SendMessageModalProps) {
  const { clearModal } = useModal();
  const { enqueueToast } = useToast();
  const { isImpersonator } = useContext(AppContext);
  const queryClient = useQueryClient();
  const contactsPageQueryParams = useContactsPageConversationsQueryParams(currentRowsPerPage, currentStart);

  const [messageInputText, setMessageInputText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Load existing message input text from local storage
    const storedText = localStorageSafeGetItem(
      `${LOCAL_STORAGE_KEY_PREFIX.MESSAGE_DRAFT}${conversationId}`,
      '',
      logger,
    );

    if (storedText && storedText.trim()) {
      setMessageInputText(storedText);
    }
  }, [conversationId]);

  // Use conversation data passed from parent
  const { renterName, mostRecentMessage } = conversation ?? {};
  const latestMessage = mostRecentMessage?.message ?? '';
  const latestMessageDateMs = mostRecentMessage?.messageDateMs;
  const latestMessageSenderName = mostRecentMessage?.senderName ?? '';
  const isSpam = false; // Contacts page doesn't include spam status

  const { data: attachmentsData } = useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata(
    {
      conversationId,
    },
    {
      meta: {
        context: {
          api: 'getAttachmentsMetadata',
          details: 'SendMessageModal - Error fetching attachments for conversation',
          listingAlias,
          conversationId,
        },
      },
    },
  );

  const { mutate: addMessage, isPending: isSendingMessage } = useSatelliteWebLandlordConversationHandler_addMessage({
    meta: {
      context: {
        api: 'addMessage',
        details: 'SendMessageModal - Error adding message to conversation',
      },
    },
    onSuccess: (data) => {
      if (contactsPageQueryParams) {
        const queryKey =
          useSatelliteWebLandlordConversationHandler_getLatestConversationsForContactsPage.getKey(
            contactsPageQueryParams,
          );
        queryClient.setQueryData<SatelliteWebLandlordGetLatestForContactsPageResponseDto>(queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            conversations: oldData.conversations?.map(
              (conv): SatelliteWebLandlordGetLatestForContactsPageConversationDto =>
                conv.conversationId === conversationId
                  ? {
                      ...conv,
                      mostRecentMessage: {
                        message: data.message ?? messageInputText.trim(),
                        messageDateMs: data.messageDateMs,
                        senderName: data.senderName || 'You',
                      },
                      hasUnreadMessage: false,
                    }
                  : conv,
            ),
          };
        });
      }

      enqueueToast(<Toast tone="success">Message sent</Toast>);
      setMessageInputText('');
      clearModal();
    },
    onError: () => {
      setErrorMessage('Message failed to send.');
    },
  });

  const attachments = attachmentsData?.attachmentMetadataDtos;
  const leadNameDisplay = renterName || '';
  const sendNameDisplay = latestMessageSenderName;
  const inboxPagePath = `${BASE_PATH}/${listingAlias}/${conversationId}?byProperty=true`;
  const hasAttachments = attachments ? attachments?.length > 0 : false;
  const shouldShowToggleButton = latestMessage.split(' ').length >= VISIBLE_WORDS_COUNT;
  const timestamp = latestMessageDateMs ? format(new Date(+latestMessageDateMs), 'yyyy MMM d h:mm a') : '';
  const viewAttachmentsText = useMemo(
    () => stringUtils.pluralize('View attachment', attachments?.length || 0),
    [attachments?.length],
  );
  const tooltipText = useMemo(() => {
    if (isImpersonator) {
      return StringConstants.TOOLTIP.IMPERSONATION;
    } else if (isSpam) {
      return StringConstants.TOOLTIP.REPORTED;
    }
    return '';
  }, [isSpam, isImpersonator]);
  const shouldShowTooltip = (isImpersonator && !!tooltipText) || (isSpam && !!tooltipText);
  const isMessageInputEmpty = formValidationUtils.isInputEmpty(messageInputText);

  const [showSendMessageSection, setShowSendMessageSection] = useState(false);

  const handleMessageInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (errorMessage) {
      setErrorMessage('');
    }

    const inputValue = event.target.value;
    return setMessageInputText(inputValue);
  };

  const handleSendClick = () => {
    if (isMessageInputEmpty) {
      setErrorMessage('A message is required to send.');
      return;
    }

    addMessage({
      postData: {
        conversationId,
        message: messageInputText.trim(),
      },
    });
  };

  const handleClose = () => {
    // Save existing message input text to local storage
    if (messageInputText) {
      localStorageSafeSetItem(`${LOCAL_STORAGE_KEY_PREFIX.MESSAGE_DRAFT}${conversationId}`, messageInputText, logger);
    } else {
      localStorageSafeRemoveItem(`${LOCAL_STORAGE_KEY_PREFIX.MESSAGE_DRAFT}${conversationId}`, logger);
    }

    clearModal();
  };

  const handleViewInMessagesClick = () => {};

  const handleShowSendMessageSection = () => {
    setShowSendMessageSection(true);
  };

  const handleHideSendMessageSection = () => {
    setShowSendMessageSection(false);
  };

  return (
    <Modal
      open={true}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
      size={{
        base: 'fullScreen',
        md: 'xs',
      }}
      header={
        <Heading level={2} textStyle="heading-xs">
          Latest contact
        </Heading>
      }
      body={
        <>
          <Paragraph textStyle="body-xs-bold" css={{ color: 'text.subtle' }}>
            Lead Name
          </Paragraph>
          <Paragraph textStyle="body-bold" css={{ marginBottom: 'default', textTransform: 'capitalize' }}>
            {leadNameDisplay}
          </Paragraph>

          <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Paragraph textStyle="body-sm-bold" css={{ color: 'coolGray.500', textTransform: 'capitalize' }}>
              {sendNameDisplay}
            </Paragraph>
            <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
              {timestamp}
            </Paragraph>
          </Box>

          <Box
            css={{ marginTop: 'tighter', backgroundColor: 'gray.100', padding: 'default', borderRadius: 'radii.xxs' }}
          >
            <Paragraph
              textStyle="body-sm"
              css={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
              }}
            >
              <ShowHideWordCount
                text={`"${latestMessage}" `}
                wordCount={VISIBLE_WORDS_COUNT}
                // Only shows the toggle button if the message is longer than the threshold
                renderButton={shouldShowToggleButton ? undefined : () => null}
              />
            </Paragraph>

            {hasAttachments && (
              <TextButton
                asChild
                textStyle="body-sm-bold"
                icon={<IconExternalOutline />}
                iconPosition="end"
                css={{ marginTop: 'tighter' }}
              >
                <a href={inboxPagePath}>{viewAttachmentsText}</a>
              </TextButton>
            )}
          </Box>

          {showSendMessageSection && (
            <>
              <Divider css={{ marginTop: 'default' }} />

              <FormField
                control={
                  <Textarea
                    autoFocus={true}
                    onChange={handleMessageInputChange}
                    placeholder="Write message..."
                    value={messageInputText}
                    rows={5}
                  />
                }
                error={!!errorMessage}
                label={<Label>Write reply</Label>}
                css={{ marginTop: 'default' }}
              />

              <Paragraph textStyle="body-xs">
                To view and send attachments, <Anchor href={inboxPagePath}>view in messages</Anchor>
              </Paragraph>
            </>
          )}
        </>
      }
      footer={
        <>
          {showSendMessageSection ? (
            <Box css={{ width: '100%' }}>
              <TooltipWrapper wrapInTooltip={shouldShowTooltip} tooltipText={tooltipText} tooltipPlacement="top">
                <ButtonGroup fluid={true} aria-label="Modal actions" css={{ width: '100%' }}>
                  <Button emphasis="outlined" onClick={handleHideSendMessageSection} size="sm" tone="brand">
                    Back
                  </Button>
                  <Button
                    emphasis="filled"
                    disabled={isSendingMessage || isImpersonator || isMessageInputEmpty}
                    onClick={handleSendClick}
                    size="sm"
                    tone="brand"
                  >
                    {isSendingMessage ? 'Sending...' : 'Send'}
                  </Button>
                </ButtonGroup>
              </TooltipWrapper>

              {!!errorMessage && (
                <InlineFeedback tone="critical" css={{ marginLeft: 'auto', marginTop: 'default' }}>
                  {errorMessage}
                </InlineFeedback>
              )}
            </Box>
          ) : (
            <ButtonGroup fluid={true} aria-label="Modal actions" css={{ width: '100%' }}>
              <Button asChild emphasis="outlined" onClick={handleViewInMessagesClick} size="sm" tone="brand">
                <a href={inboxPagePath}>View in messages</a>
              </Button>
              <Button emphasis="filled" onClick={handleShowSendMessageSection} size="sm" tone="brand">
                Reply
              </Button>
            </ButtonGroup>
          )}
        </>
      }
    />
  );
}
